import { useEffect, useRef } from 'react'
import { Form } from 'antd'
import { Popup } from 'components/Popup'
import { Input } from 'components/Input'
import { Button, BUTTON_TYPES } from 'components/Button'
import { Typography } from 'components/Typography'
import { BTN_TXT, LABELS } from 'constants/txt'
import { validateMessages } from 'helper/common'
import { ReactComponent as Plus16 } from 'assets/svg/Plus16.svg'
import { ReactComponent as Delete16 } from 'assets/svg/Delete16.svg'
import { useContractDetailsPopup } from 'features/ContractDetails/Providers/ContractDetailsPopupProvider'
import { PROPS_INPUT_NUMBER } from 'features/ContractDetails/RebateConditions/RebateConditionsForm/constants'
import {
  isEmptyValue,
  percentageRebateValidator,
  positiveNumberValidator,
  sequentialTierThresholdValidator
} from 'features/ContractDetails/RebateConditions/RebateConditionsForm/utils'
import {
  EMPTY_REBATE_TIER,
  MAX_REBATE_TIERS,
  PURCHASE_VOLUME_THRESHOLD_MEASURED_IN
} from '../constants'
import { RebateTier } from '../types'
import './styles.scss'

type TiersForm = {
  tiers: RebateTier[]
}

type EditRebateTiersPopupProps = {
  thresholdMeasuredIn?: string
  tiers: RebateTier[]
  onSave: (tiers: RebateTier[]) => Promise<boolean>
}

const EditRebateTiersPopup = ({
  thresholdMeasuredIn,
  tiers,
  onSave
}: EditRebateTiersPopupProps) => {
  const [popupForm] = Form.useForm<TiersForm>()
  const addButtonRef = useRef<HTMLDivElement>(null)
  const {
    editRebateTiersPopup: {
      state,
      actions: { close }
    }
  } = useContractDetailsPopup()

  const isPurchaseVolume =
    thresholdMeasuredIn === PURCHASE_VOLUME_THRESHOLD_MEASURED_IN
  const initialTiers = tiers.length
    ? tiers.map((tier) => ({ ...tier }))
    : [{ ...EMPTY_REBATE_TIER }]

  useEffect(() => {
    if (!state.visible) return

    popupForm.resetFields()
    popupForm.setFieldsValue({ tiers: initialTiers })
    // Reload saved tiers each time the popup opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.visible])

  const handleFinish = async (values: TiersForm) => {
    const isSaved = await onSave(values.tiers)
    if (isSaved) close()
  }

  const revalidateThresholds = () => {
    const tiers: RebateTier[] = popupForm.getFieldValue('tiers') || []
    const paths = tiers.flatMap((tier, index) =>
      isEmptyValue(tier?.threshold) ? [] : [['tiers', index, 'threshold']]
    )

    if (!paths.length) return

    void popupForm.validateFields(paths).catch(() => undefined)
  }

  return (
    <Popup
      {...state}
      width={676}
      className="edit-rebate-tiers-popup"
      footer={
        <>
          <Button
            type={BUTTON_TYPES.DEFAULT}
            onClick={close}
            upperCase
            htmlType="button"
          >
            {BTN_TXT.CANCEL}
          </Button>
          <Button
            type={BUTTON_TYPES.PRIMARY}
            onClick={popupForm.submit}
            upperCase
            htmlType="button"
          >
            {BTN_TXT.SAVE}
          </Button>
        </>
      }
    >
      <Form<TiersForm>
        form={popupForm}
        initialValues={{ tiers: initialTiers }}
        onFinish={handleFinish}
        validateMessages={validateMessages}
        onValuesChange={revalidateThresholds}
      >
        <Form.List name="tiers">
          {(fields, { add, remove }) => (
            <div className="edit-rebate-tiers-popup__list">
              {fields.map(({ key, name, ...rest }, index) => (
                <div className="edit-rebate-tiers-popup__row" key={key}>
                  <Typography.Body1 className="edit-rebate-tiers-popup__tier">
                    Tier {index + 1}
                  </Typography.Body1>
                  <Input.Number
                    propsItem={{
                      ...rest,
                      name: [name, 'threshold'],
                      label: isPurchaseVolume
                        ? LABELS.THRESHOLD_DOLLARS
                        : LABELS.THRESHOLD,
                      dependencies:
                        index > 0
                          ? [['tiers', index - 1, 'threshold']]
                          : undefined,
                      rules: [
                        { required: true },
                        {
                          validator: async (_, value) => {
                            if (isEmptyValue(value)) return Promise.resolve()

                            if (index > 0) {
                              const previous = popupForm.getFieldValue([
                                'tiers',
                                index - 1,
                                'threshold'
                              ])
                              await sequentialTierThresholdValidator(
                                value,
                                previous
                              )
                            }

                            return isPurchaseVolume
                              ? positiveNumberValidator(_, value)
                              : percentageRebateValidator(_, value)
                          }
                        }
                      ]
                    }}
                    propsInputNumber={PROPS_INPUT_NUMBER}
                  />
                  <Input.Number
                    propsItem={{
                      ...rest,
                      name: [name, 'amount'],
                      label: LABELS.REBATE_AMOUNT,
                      rules: [
                        { required: true },
                        {
                          validator: (_, value) =>
                            isEmptyValue(value)
                              ? Promise.resolve()
                              : percentageRebateValidator(_, value)
                        }
                      ]
                    }}
                    propsInputNumber={PROPS_INPUT_NUMBER}
                  />
                  <Button.Icon
                    className="edit-rebate-tiers-popup__remove"
                    type={BUTTON_TYPES.LINK}
                    icon={<Delete16 />}
                    danger
                    small
                    htmlType="button"
                    disabled={fields.length === 1}
                    onClick={() => remove(name)}
                  />
                </div>
              ))}
              <div ref={addButtonRef}>
                <Button
                  className="edit-rebate-tiers-popup__add-btn"
                  type={BUTTON_TYPES.GHOST}
                  icon={<Plus16 />}
                  upperCase
                  htmlType="button"
                  disabled={fields.length >= MAX_REBATE_TIERS}
                  onClick={() => {
                    if (fields.length >= MAX_REBATE_TIERS) return
                    add({ ...EMPTY_REBATE_TIER })
                    requestAnimationFrame(() => {
                      addButtonRef.current?.scrollIntoView({
                        block: 'nearest'
                      })
                    })
                  }}
                >
                  Add tier
                </Button>
              </div>
            </div>
          )}
        </Form.List>
      </Form>
    </Popup>
  )
}

export default EditRebateTiersPopup
